import { CSS, Spacing, StyleProps } from "@shared/primitive-components"
import { Property } from "csstype"
import { FeedbackCondition } from "../types"

export const separator = (marginBottom: Spacing | null) => (props: StyleProps) => ({
  marginTop: props.theme.spacing(2),
  marginBottom: marginBottom ? props.theme.spacing(marginBottom) : 0,
})

export const safetyContainer = () => ({
  background: `transparent`,
  padding: 0,
})

export const safetyWrap = (props: StyleProps) => ({
  marginBottom: props.theme.spacing(4),
  padding: 0,
  width: `100%`,
  "& .mdc-layout-grid__inner": {
    "@media (min-width: 828px)": {
      "@supports(display: grid)": {
        gridTemplateColumns: `repeat(10, minmax(0, 1fr))`,
      },
    },
    [props.theme.breakPoints.lg.up]: {
      "@supports(display: grid)": {
        gridTemplateColumns: `repeat(9, minmax(0, 1fr))`,
      },
    },
    [props.theme.breakPoints.xl.up]: {
      "@supports(display: grid)": {
        gridTemplateColumns: `repeat(10, minmax(0, 1fr))`,
      },
    },
    [props.theme.breakPoints.xxl.up]: {
      "@supports(display: grid)": {
        gridTemplateColumns: `repeat(9, minmax(0, 1fr))`,
      },
    },
    [props.theme.breakPoints.xxxl.up]: {
      "@supports(display: grid)": {
        gridTemplateColumns: `repeat(10, minmax(0, 1fr))`,
      },
    },
  },
})

export const safetyItemLeft = (props: StyleProps) => ({
  ...safetyItem(props),
  "@media (min-width: 828px)": {
    "@supports(display: grid)": {
      width: `auto`,
      gridColumnEnd: `span 5`,
    },
  },
  [props.theme.breakPoints.lg.up]: {
    "@supports(display: grid)": {
      width: `auto`,
      gridColumnEnd: `span 4`,
    },
  },
  [props.theme.breakPoints.xl.up]: {
    "@supports(display: grid)": {
      width: `auto`,
      gridColumnEnd: `span 4`,
    },
  },
  [props.theme.breakPoints.xxl.up]: {
    "@supports(display: grid)": {
      width: `auto`,
      gridColumnEnd: `span 3`,
    },
  },
  [props.theme.breakPoints.xxxl.up]: {
    "@supports(display: grid)": {
      width: `auto`,
      gridColumnEnd: `span 3`,
    },
  },
})

export const safetyItemRight = (props: StyleProps) => ({
  ...safetyItem(props),
  "@media (min-width: 828px)": {
    "@supports(display: grid)": {
      width: `auto`,
      gridColumnEnd: `span 5`,
    },
  },
  [props.theme.breakPoints.lg.up]: {
    "@supports(display: grid)": {
      width: `auto`,
      gridColumnEnd: `span 5`,
    },
  },
  [props.theme.breakPoints.xl.up]: {
    "@supports(display: grid)": {
      width: `auto`,
      gridColumnEnd: `span 6`,
    },
  },
  [props.theme.breakPoints.xxl.up]: {
    "@supports(display: grid)": {
      width: `auto`,
      gridColumnEnd: `span 6`,
    },
  },
  [props.theme.breakPoints.xxxl.up]: {
    "@supports(display: grid)": {
      width: `auto`,
      gridColumnEnd: `span 7`,
    },
  },
})

export const safetyItem = (props: StyleProps) => ({
  width: `100%`,
  padding: props.theme.spacing(6),
  backgroundColor: props.theme.palette.white,
  height: `100%`,
  minHeight: `426px`,
  "@supports(display: grid)": {
    width: `auto`,
    gridColumnEnd: `span 9`,
  },
})

export const gridWrap = (): CSS => ({
  width: `100%`,
})

export const boxWrap = (): CSS => ({
  ...gridWrap(),
  display: `flex`,
  flexDirection: `column`,
})

export const statCardTitle = () => ({
  display: `inline-block`,
})

export const subTitleSeparator = () => ({
  marginBottom: `30px`,
})

export const cardWrap = (): CSS => ({
  height: `100%`,
})

export const text = (props: StyleProps): CSS => ({
  marginBottom: props.theme.spacing(6),
})

export const textDetail = (props: StyleProps): CSS => ({
  fontSize: props.theme.fontSize.caption,
  marginTop: props.theme.spacing(6),
  ...text(props),
})

export const rebateStat =
  (disabled: boolean | undefined) =>
  (props: StyleProps): CSS => ({
    color: !disabled ? props.theme.palette.coal.dark : props.theme.palette.grey.light,
    textDecoration: !disabled ? `none` : `line-through ${props.theme.palette.full.red}`,
  })

export const rebateStatUnit =
  (lowercaseLabel: boolean | undefined) =>
  (props: StyleProps): CSS => ({
    ...props.theme.fonts.heading,
    textTransform: lowercaseLabel ? `lowercase` : props.theme.fonts.heading.textTransform,
    fontSize: props.theme.fontSize.subtitle,
  })

const getConditionalFeedbackColor =
  (condition: FeedbackCondition) =>
  (props: StyleProps): Property.Color =>
    condition === `positive`
      ? props.theme.palette.full.green
      : condition === `negative`
      ? props.theme.palette.full.red
      : props.theme.palette.full.orange

const conditionalColor =
  (isPositive: boolean) =>
  (props: StyleProps): CSS => ({
    color: getConditionalFeedbackColor(isPositive ? `positive` : `negative`)(props),
  })

export const feedbackIcon =
  (condition: FeedbackCondition) =>
  (props: StyleProps): CSS => ({
    color: getConditionalFeedbackColor(condition)(props),
    marginRight: props.theme.spacing(3),
  })

export const rebateStatChangeFigure =
  (isPositive: boolean) =>
  (props: StyleProps): CSS => ({
    ...conditionalColor(isPositive)(props),
    ...props.theme.fonts.subHeading,
    marginBottom: props.theme.spacing(2),
    display: `inline-flex`,
    alignItems: `center`,
  })

export const marginBottom =
  (margin: Spacing) =>
  (props: StyleProps): CSS => ({
    marginBottom: props.theme.spacing(margin),
  })

export const tooltipContent = (props: StyleProps): CSS => ({
  maxWidth: `200px`,
  marginBottom: props.theme.spacing(2),
})

export const marginTop =
  (margin: Spacing) =>
  (props: StyleProps): CSS => ({
    marginTop: props.theme.spacing(margin),
  })

export const marginX =
  (margin: Spacing) =>
  (props: StyleProps): CSS => ({
    marginLeft: props.theme.spacing(margin),
    marginRight: props.theme.spacing(margin),
  })

export const marginY =
  (margin: Spacing) =>
  (props: StyleProps): CSS => ({
    marginTop: props.theme.spacing(margin),
    marginBottom: props.theme.spacing(margin),
  })

export const disclaimerText = (props: StyleProps): CSS => ({
  fontSize: props.theme.fontSize.caption,
  color: props.theme.palette.white,
})

export const tooltipHeadingStyle = (props: StyleProps) => ({
  ...props.theme.fonts.subHeading,
  marginBottom: props.theme.spacing(4),
})

export const statFeedback = (props: StyleProps) => ({
  margin: props.theme.spacing(0, 0, 1, 4),
})

export const buttonLinkStyle = (): CSS => ({
  marginTop: `auto`,
})

export const marginLeft =
  (margin: Spacing) =>
  (props: StyleProps): CSS => ({
    marginLeft: props.theme.spacing(margin),
  })

export const marginRight =
  (margin: Spacing) =>
  (props: StyleProps): CSS => ({
    marginRight: props.theme.spacing(margin),
  })

export const disclaimerLink = (props: StyleProps): CSS => ({
  marginLeft: props.theme.spacing(1),
  color: props.theme.palette.white,
})
